// The columns a tile's visual shows, read from its saved model — what a click
// on the tile can land on, so a drill-through can answer to one of them
// (DRILL_THROUGH[].SOURCE_COLUMN). Read from LAYOUT_DATA.visualLayout's
// ColumnGroups (the visual's drop zones); hidden columns are left out because
// nobody can click them. Never throws.

export interface VisualColumn {
  /** The column NAME — what rows are keyed by and SOURCE_COLUMN stores. */
  name: string
  /** The column's label as authored. */
  label: string
  /** The zone it sits in (Rows / Columns / Values / Series / …). */
  group: string
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : v == null ? '' : String(v)
}

function layoutOf(content: unknown): Record<string, unknown> | null {
  if (!content || typeof content !== 'object') return null
  const c = content as Record<string, unknown>
  const ld = c.LAYOUT_DATA as Record<string, unknown> | undefined
  const vl = (ld && typeof ld === 'object' ? ld.visualLayout : undefined) ?? c.visualLayout
  return vl && typeof vl === 'object' ? (vl as Record<string, unknown>) : null
}

export function visualColumns(content: unknown): VisualColumn[] {
  const out: VisualColumn[] = []
  try {
    const vl = layoutOf(content)
    const groups = Array.isArray(vl?.ColumnGroups) ? (vl!.ColumnGroups as unknown[]) : []
    for (const g of groups) {
      if (!g || typeof g !== 'object') continue
      const gr = g as Record<string, unknown>
      const group = str(gr.StandardName || gr.GroupName)
      const cols = Array.isArray(gr.COLUMNS) ? (gr.COLUMNS as unknown[]) : []
      for (const c of cols) {
        if (!c || typeof c !== 'object') continue
        const col = c as Record<string, unknown>
        const name = str(col.NAME || col.name).trim()
        if (!name || out.some((o) => o.name.toLowerCase() === name.toLowerCase())) continue
        const hidden = col.HIDDEN === true || ['true', 'y', '1', 'yes'].includes(str(col.HIDDEN).toLowerCase())
        if (hidden) continue
        const label = str(col.LABEL || col.name || col.content || col.descr).trim() || name
        out.push({ name, label, group })
      }
    }
  } catch (err) {
    console.warn('Could not read the columns of a tile visual', err)
  }
  return out
}

/** Every column name the visual's rows carry (hidden included) — the source
 *  columns a drill-through's PARAM_MAPPING can read from the clicked row. */
export function visualRowColumns(content: unknown): string[] {
  const out: string[] = []
  try {
    const vl = layoutOf(content)
    const groups = Array.isArray(vl?.ColumnGroups) ? (vl!.ColumnGroups as unknown[]) : []
    for (const g of groups) {
      const cols = g && typeof g === 'object' && Array.isArray((g as Record<string, unknown>).COLUMNS)
        ? ((g as Record<string, unknown>).COLUMNS as unknown[]) : []
      for (const c of cols) {
        const name = c && typeof c === 'object' ? str((c as Record<string, unknown>).NAME).trim() : ''
        if (name && !out.includes(name)) out.push(name)
      }
    }
  } catch { /* none */ }
  return out
}
